import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormHelperText, FormLabel } from "@mui/material";
import type { FileUploadProps } from "@components/FileUpload/FileUpload.types";
import FileUploadItem from "@components/FileUpload/FileUploadItem";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import { t } from "i18next";
import { useDropzone, type FileRejection } from "react-dropzone";

const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];

const normalizeExtension = (ext: string) => {
  const withDot = ext.startsWith(".") ? ext : "." + ext;
  return withDot.toLowerCase();
};

const mergeUnique = (list: string[]) => Array.from(new Set(list));

const buildTestId = (base: string | undefined, suffix: string) =>
  base ? base + "-" + suffix : undefined;

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  labelId,
  onUpload,
  onRemoveFile,
  onReject,
  testId,
  conditions,
  name,
  maxFiles,
  allowedExtensions,
  disabled,
  allowReplaceExisting = false,
  uploadStoreKey,
  files,
  setFiles,
  required,
  uploadDescription,
  isSideDrawer,
  validateFiles,
  onValidationResult,
  validateDrop,
  isError,
}) => {
  void onRemoveFile;
  const normalizedAllowedExtensions = useMemo(
    () => mergeUnique(allowedExtensions.map(normalizeExtension)),
    [allowedExtensions],
  );
  const allowedExtensionsDisplay = useMemo(
    () => normalizedAllowedExtensions.join(", "),
    [normalizedAllowedExtensions],
  );

  const storedFiles = useUploadStore((state) =>
    uploadStoreKey
      ? (state.filesByKey[uploadStoreKey] ?? EMPTY_UPLOAD_ENTRIES)
      : name
        ? (state.filesByKey[name] ?? EMPTY_UPLOAD_ENTRIES)
        : EMPTY_UPLOAD_ENTRIES,
  );
  const currentFiles = files && files.length ? files : storedFiles;

  const maxFilesLimit = Math.max(1, maxFiles);
  const isSingleFileMode = maxFilesLimit === 1;

  const isAllowedType = useCallback(
    (file: File) => {
      const extension = normalizeExtension(
        file.name.substring(file.name.lastIndexOf(".")) || file.name,
      );
      return normalizedAllowedExtensions.includes(extension);
    },
    [normalizedAllowedExtensions],
  );

  const isUnderMaxSize = useCallback(
    (file: File) => {
      const maxSize = conditions?.maxSizeBytes;
      return maxSize ? file.size <= maxSize : true;
    },
    [conditions],
  );

  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileTypeError, setFileTypeError] = useState(false);

  const isAtCapacity = currentFiles.length >= maxFilesLimit;
  const isDisabled = disabled || (isAtCapacity && !(allowReplaceExisting && isSingleFileMode));

  const syncFilesToStore = useCallback(
    (incoming: UploadEntry[], snapshot: UploadEntry[]) => {
      const storeKey = uploadStoreKey ?? name;
      if (!storeKey) return;
      const store = useUploadStore.getState();

      if (isSingleFileMode) {
        store.setFilesForKey(storeKey, snapshot, { mode: "replace", maxFiles: 1 });
        return;
      }

      store.setFilesForKey(storeKey, incoming, { mode: "merge", maxFiles: maxFilesLimit });
    },
    [isSingleFileMode, maxFilesLimit, name, uploadStoreKey],
  );

  const processFiles = useCallback(
    async (newFiles: File[]) => {
      const filtered = newFiles.filter((file) => isUnderMaxSize(file) && isAllowedType(file));
      const rejectedFiles = newFiles.filter((file) => !isUnderMaxSize(file));
      const rejectedType = newFiles.filter((file) => !isAllowedType(file));

      setFileTypeError(rejectedType.length > 0);
      setFileSizeError(rejectedFiles.length > 0);

      if (filtered.length === 0) {
        if (onReject) onReject();
        return;
      }

      if (validateFiles) {
        const validationResult = await validateFiles(filtered);
        const message =
          typeof validationResult === "string"
            ? validationResult.trim().length > 0
              ? validationResult
              : null
            : (validationResult ?? null);

        if (message) {
          onValidationResult?.(message);
          return;
        }
        onValidationResult?.(null);
      }

      const existingEntries = currentFiles ?? [];
      const existingFileObjects = existingEntries.filter(
        (item): item is File => item instanceof File,
      );

      const buildNextState = (
        incomingEntries: UploadEntry[],
        incomingFiles: File[],
      ): { nextEntries: UploadEntry[]; uploadPayload: File[] } => {
        if (isSingleFileMode) {
          const replacementEntry =
            incomingEntries[incomingEntries.length - 1] ??
            existingEntries[existingEntries.length - 1];
          const replacementFile =
            incomingFiles[incomingFiles.length - 1] ??
            existingFileObjects[existingFileObjects.length - 1];

          const nextEntries = replacementEntry ? [replacementEntry] : [];
          const uploadPayload = replacementFile ? [replacementFile] : [];
          return { nextEntries, uploadPayload };
        }

        const combinedEntries = [...existingEntries, ...incomingEntries];
        const combinedFiles = [...existingFileObjects, ...incomingFiles];

        const nextEntries =
          combinedEntries.length > maxFilesLimit
            ? combinedEntries.slice(combinedEntries.length - maxFilesLimit)
            : combinedEntries;
        const uploadPayload =
          combinedFiles.length > maxFilesLimit
            ? combinedFiles.slice(combinedFiles.length - maxFilesLimit)
            : combinedFiles;

        return { nextEntries, uploadPayload };
      };

      const { nextEntries, uploadPayload } = buildNextState(filtered, filtered);
      setFiles(nextEntries);
      syncFilesToStore(filtered, nextEntries);
      if (onUpload) onUpload(uploadPayload);
    },
    [
      currentFiles,
      isAllowedType,
      isUnderMaxSize,
      maxFilesLimit,
      onReject,
      onUpload,
      setFiles,
      isSingleFileMode,
      syncFilesToStore,
    ],
  );

  useEffect(() => {
    if ((!name && !uploadStoreKey) || currentFiles.length) return;
    if (storedFiles.length) {
      setFiles(storedFiles);
    }
  }, [currentFiles.length, name, setFiles, storedFiles, uploadStoreKey]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const selectedCount = acceptedFiles.length + fileRejections.length;
      const dropError = validateDrop?.(selectedCount);
      if (dropError) {
        onValidationResult?.(dropError);
        return;
      }

      await processFiles(acceptedFiles);
    },
    [processFiles, validateDrop, onValidationResult],
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    multiple: maxFilesLimit > 1,
    maxFiles: maxFilesLimit,
    accept: conditions?.accept,
    disabled: isDisabled,
  });

  const displayErrors = useMemo(() => !conditions?.hideErrors, [conditions?.hideErrors]);
  const showErrorState = !isSideDrawer && (fileSizeError || fileTypeError || isError);

  const fileTypeErrorTestId = buildTestId(testId, "file-size-or-type-error");

  return (
    <div className="flex flex-col gap-2 w-full">
      <FormLabel
        data-testid={labelId}
        className="text-primary"
        sx={(theme) => ({
          color: theme.palette.text.primary,
          fontWeight: theme.typography.fontWeightMedium,
        })}
      >
        {label}
        {required ? " *" : ""}
      </FormLabel>
      <FileUploadItem
        testId={testId}
        open={open}
        isDragActive={isDragActive}
        getInputProps={getInputProps}
        getRootProps={getRootProps}
        uploadDescription={uploadDescription}
        disabled={isDisabled}
        hasError={showErrorState}
      />
      {(fileTypeError || fileSizeError) && displayErrors && (
        <div data-testid={fileTypeErrorTestId}>
          <FormHelperText
            sx={{
              ml: 0,
              fontWeight: 500,
              fontSize: 12,
              marginY: "4px",
              color: "error.main",
            }}
          >
            {t("form-validation.file-type", { types: allowedExtensionsDisplay })}
          </FormHelperText>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
export { FileUpload };
export const DocumentUpload = FileUpload;
export const CertificateUpload = FileUpload;
